<template>
  <v-dialog
    :value="value"
    max-width="640"
    persistent
    eager
    content-class="search-radius-dialog"
    @input="$emit('input', $event)"
  >
    <v-card class="search-radius-dialog__card" dark>
      <v-card-title class="search-radius-dialog__title">
        <v-icon left small>mdi-map-marker-radius</v-icon>
        Seleccionar radio
      </v-card-title>
      <v-card-text class="search-radius-dialog__body">
        <div ref="lienzo" class="search-radius-dialog__map"></div>
        <div class="search-radius-dialog__hint">Arrastra el mapa para mover el centro.</div>
        <div class="search-radius-dialog__value">
          Radio: <strong>{{ radius }}</strong> m
        </div>
        <v-slider
          v-model="radius"
          :min="100"
          :max="5000"
          :step="100"
          thumb-label
          hide-details
          color="var(--kln-accent)"
          @change="encuadrar"
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="$emit('input', false)">Cancelar</v-btn>
        <v-btn color="var(--kln-accent)" depressed @click="confirmar">Aceptar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
// Selector de centro y radio para la búsqueda inteligente de Map V2.
// Sustituye en el panel a components/map/RadiusPickerDialog.vue, que dibuja con
// OpenLayers: /map no carga OpenLayers (router/index.js) y aquel diálogo salía
// gris y su «Aceptar» cerraba sin emitir nada. Mismo contrato que aquel:
// emite `confirm` con { center: [lng, lat], radiusMeters }.
import { createMap } from '@/components/map-v2/core/maplibre-init'
import { createGeoCircle, geoCircleBounds } from '@/components/map-v2/utils/geo-circle'

const FUENTE = 'radio-busqueda'

// MapLibre no resuelve `var(--kln-*)` en las propiedades de pintura.
function token(nombre) {
  return getComputedStyle(document.documentElement).getPropertyValue(nombre).trim()
}

export default {
  name: 'SearchRadiusDialog',
  props: {
    value: { type: Boolean, default: false },
    getCenter: { type: Function, default: null },
    getZoom: { type: Function, default: null },
    initialRadius: { type: Number, default: 500 },
  },
  data() {
    return { radius: 500 }
  },
  watch: {
    value(abierto) {
      if (abierto) {
        this.radius = this.initialRadius
        this.$nextTick(() => this.crearMapa())
      } else {
        this.destruirMapa()
      }
    },
    radius() { this.pintar() },
  },
  created() {
    // Fuera de data(): el mapa no debe volverse reactivo.
    this.mapa = null
    this.listo = false
  },
  beforeDestroy() {
    this.destruirMapa()
  },
  methods: {
    centro() {
      if (this.mapa) {
        const c = this.mapa.getCenter()
        return [c.lng, c.lat]
      }
      return this.getCenter ? this.getCenter() : null
    },
    crearMapa() {
      if (this.mapa || !this.$refs.lienzo) return
      this.mapa = createMap(this.$refs.lienzo, {
        styleId: 'dark',
        apiKey: import.meta.env.VITE_MAPTILER_KEY,
        center: this.getCenter ? this.getCenter() : undefined,
        zoom: this.getZoom ? this.getZoom() : undefined,
      })
      if (!this.mapa) return
      this.mapa.on('load', () => {
        this.listo = true
        this.mapa.resize()
        const acento = token('--kln-accent')
        const conColor = (paint, prop) => (acento ? { ...paint, [prop]: acento } : paint)
        this.mapa.addSource(FUENTE, { type: 'geojson', data: this.geojson() })
        this.mapa.addLayer({
          id: `${FUENTE}-relleno`,
          type: 'fill',
          source: FUENTE,
          filter: ['==', ['geometry-type'], 'Polygon'],
          paint: conColor({ 'fill-opacity': 0.12 }, 'fill-color'),
        })
        this.mapa.addLayer({
          id: `${FUENTE}-borde`,
          type: 'line',
          source: FUENTE,
          filter: ['==', ['geometry-type'], 'Polygon'],
          paint: conColor({ 'line-width': 2, 'line-dasharray': [4, 3] }, 'line-color'),
        })
        this.mapa.addLayer({
          id: `${FUENTE}-centro`,
          type: 'circle',
          source: FUENTE,
          filter: ['==', ['geometry-type'], 'Point'],
          paint: conColor({ 'circle-radius': 4 }, 'circle-color'),
        })
        this.mapa.on('move', () => this.pintar())
        this.encuadrar()
      })
    },
    destruirMapa() {
      if (this.mapa) this.mapa.remove()
      this.mapa = null
      this.listo = false
    },
    geojson() {
      const c = this.centro()
      return {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: { type: 'Polygon', coordinates: [createGeoCircle(c, this.radius)] },
            properties: {},
          },
          { type: 'Feature', geometry: { type: 'Point', coordinates: c }, properties: {} },
        ],
      }
    },
    pintar() {
      if (!this.listo) return
      const fuente = this.mapa.getSource(FUENTE)
      if (fuente) fuente.setData(this.geojson())
    },
    // Al abrir y al soltar el deslizador: el círculo entero a la vista. El
    // centro no se mueve, porque la caja es simétrica alrededor de él.
    encuadrar() {
      if (!this.listo) return
      this.mapa.fitBounds(geoCircleBounds(this.centro(), this.radius), { padding: 24, duration: 0 })
    },
    confirmar() {
      const center = this.centro()
      if (center) this.$emit('confirm', { center, radiusMeters: this.radius })
      this.$emit('input', false)
    },
  },
}
</script>

<style scoped>
.search-radius-dialog__card {
  background: var(--kln-bg-surface) !important;
  border: 1px solid var(--kln-border-dim);
}

.search-radius-dialog__title {
  font-size: 14px;
  color: var(--kln-text);
  border-bottom: 1px solid var(--kln-border-dim);
}

.search-radius-dialog__body {
  padding: 16px !important;
}

.search-radius-dialog__map {
  width: 100%;
  height: 320px;
  background: var(--kln-bg-deep);
}

.search-radius-dialog__hint {
  margin-top: 6px;
  font-size: 11px;
  color: var(--kln-text-dim);
}

.search-radius-dialog__value {
  margin-top: 12px;
  font-size: 13px;
  color: var(--kln-text-secondary);
}

.search-radius-dialog__value strong {
  font-size: 18px;
  color: var(--kln-text);
}
</style>
